import { Link, useLocation } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

const SEGMENT_LABELS: Record<string, string> = {
  '': 'Dashboard',
  stories: 'Stories',
  vulnerabilities: 'Vulnerabilities',
  'threat-actors': 'Threat Actors',
  // /threats is a section parent (no standalone page) but appears in
  // /threats/leak-sites and /threats/supply-chain breadcrumb trails.
  // P12 audit (2026-05-02) found these missing: the trail rendered
  // "Dashboard > Details > Leak Sites" instead of "Dashboard > Threats >
  // Leak Sites".
  threats: 'Threats',
  'leak-sites': 'Leak Sites',
  'supply-chain': 'Supply Chain',
  map: 'Live Map',
  analytics: 'Analytics',
  benchmarks: 'Benchmarks',
  regulatory: 'Regulatory',
  calendar: 'Regulatory Calendar',
  compare: 'Regulatory Compare',
  'regulatory-actions': 'Regulatory Actions',
  settings: 'Settings',
  admin: 'Admin',
  content: 'Content',
  sources: 'Sources',
  ai: 'AI',
  providers: 'Providers',
  tasks: 'Tasks',
  costs: 'Costs',
  email: 'Email',
  vendors: 'Vendors',
  system: 'System',
  observability: 'Observability',
  frameworks: 'Frameworks',
  'regulatory-briefs': 'Regulatory Briefs',
  'regulatory-deadlines': 'Regulatory Deadlines',
  'digest-runs': 'Digest Runs',
  new: 'New',
}

/** Title-Case a URL slug ("eu-ai-act" → "Eu Ai Act") for human-readable trails. */
function titleCaseSlug(segment: string): string {
  return decodeURIComponent(segment)
    .split(/[-/\s_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function labelFor(segment: string, parent: string | undefined): string {
  if (segment in SEGMENT_LABELS) return SEGMENT_LABELS[segment]
  // Framework-slug segment (/regulatory/frameworks/:slug). The framework name
  // isn't fetched in this route-only component, so Title-Case the slug rather
  // than falling through to the generic "Details" placeholder.
  if (parent === 'frameworks') return titleCaseSlug(segment)
  // other dynamic route segments (CVE IDs, UUIDs, etc.) → "Details"
  return 'Details'
}

export interface BreadcrumbsProps {
  className?: string
}

export function Breadcrumbs({ className }: BreadcrumbsProps) {
  const location = useLocation()
  const segments = location.pathname.split('/').filter(Boolean)

  if (segments.length === 0) return null

  const trail = [{ path: '/', label: SEGMENT_LABELS[''] }]
  for (let i = 0; i < segments.length; i++) {
    trail.push({
      path: '/' + segments.slice(0, i + 1).join('/'),
      label: labelFor(segments[i], segments[i - 1]),
    })
  }

  const displayed = trail.length > 4
    ? [trail[0], { path: '', label: '…' }, ...trail.slice(-2)]
    : trail

  return (
    <nav aria-label="Breadcrumb" className={cn('flex items-center gap-1 text-sm text-muted-foreground', className)}>
      {displayed.map((item, idx) => {
        const isLast = idx === displayed.length - 1
        const isEllipsis = item.label === '…'

        return (
          <span key={`${item.path}-${idx}`} className="flex items-center gap-1">
            {idx > 0 && <ChevronRight className="h-3 w-3" aria-hidden />}
            {isEllipsis ? (
              <span>…</span>
            ) : isLast ? (
              <span aria-current="page" className="text-foreground font-medium">
                {item.label}
              </span>
            ) : (
              <Link to={item.path} className="hover:text-foreground transition-colors">
                {item.label}
              </Link>
            )}
          </span>
        )
      })}
    </nav>
  )
}
